import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import ScrollReveal from '../animations/ScrollReveal';
import Tilt3DCard from '../animations/Tilt3DCard';
import api from '../../services/api';

interface Testimonial {
  id: number;
  name: string;
  position: string;
  company: string;
  content: string;
  rating: number;
  avatar: string;
  color: string;
}

const TestimonialsSection: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || testimonials.length === 0) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/testimonials');
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToTestimonial = (index: number) => {
    setCurrentTestimonial(index);
    setIsAutoPlaying(false);
  };

  // Show loading state if no testimonials are loaded yet
  if (loading) {
    return (
      <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </section>
    );
  }

  // Hide section if no testimonials are available
  if (testimonials.length === 0) {
    return null;
  }

  const current = testimonials[currentTestimonial];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background with animated gradient */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 animate-gradientShift"
        style={{ backgroundSize: '200% 200%' }}
      />
      
      {/* Floating background shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-gradient-to-r from-blue-300/20 to-purple-300/20 blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full bg-gradient-to-r from-pink-300/20 to-orange-300/20 blur-3xl animate-float" style={{ animationDelay: '-2s' }} />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <ScrollReveal direction="up">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6 bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-900">
              <Star className="w-4 h-4 mr-2 fill-current" />
              Client Testimonials
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="up" delay={0.1}>
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              What Our Clients
              <span className="block text-gradient">Say About Us</span>
            </h2>
          </ScrollReveal>
          
          <ScrollReveal direction="up" delay={0.2}>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our satisfied clients 
              have to say about working with us.
            </p>
          </ScrollReveal>
        </div>

        {/* Main Testimonial Card */}
        <div className="max-w-4xl mx-auto mb-16">
          <ScrollReveal direction="up" delay={0.3}>
            <Tilt3DCard>
              <div className="relative bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-100 overflow-hidden">
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-r ${current.color} opacity-5`} />
                
                {/* Quote icon */}
                <div className="relative z-10">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${current.color} mb-8`}>
                    <Quote className="w-8 h-8 text-white" />
                  </div>

                  {/* Rating */}
                  <div className="flex items-center space-x-1 mb-6">
                    {[...Array(current.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Testimonial content */}
                  <blockquote className="text-2xl md:text-3xl text-gray-900 font-medium leading-relaxed mb-8">
                    "{current.content}"
                  </blockquote>

                  {/* Author info */}
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <img
                        src={current.avatar}
                        alt={current.name}
                        className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                      <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${current.color} opacity-20`} />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-gray-900">{current.name}</div>
                      <div className="text-gray-600">{current.position}</div>
                      <div className="text-gray-500 text-sm">{current.company}</div>
                    </div>
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute top-8 right-8 w-32 h-32 rounded-full bg-gradient-to-r from-white/50 to-transparent blur-2xl" />
                <div className="absolute bottom-8 left-8 w-20 h-20 rounded-full bg-gradient-to-r from-white/30 to-transparent blur-xl" />
              </div>
            </Tilt3DCard>
          </ScrollReveal>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center space-x-8">
          <ScrollReveal direction="up" delay={0.4}>
            <button
              onClick={prevTestimonial}
              className="p-3 rounded-full bg-white shadow-lg border border-gray-200 text-gray-600 hover:text-gray-900 hover:shadow-xl transition-all duration-300 hover-lift"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          </ScrollReveal>

          {/* Dots indicator */}
          <div className="flex space-x-3">
            {testimonials.map((_, index) => (
              <ScrollReveal key={index} direction="up" delay={0.5 + index * 0.05}>
                <button
                  onClick={() => goToTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial
                      ? `bg-gradient-to-r ${current.color} scale-125`
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal direction="up" delay={0.4}>
            <button
              onClick={nextTestimonial}
              className="p-3 rounded-full bg-white shadow-lg border border-gray-200 text-gray-600 hover:text-gray-900 hover:shadow-xl transition-all duration-300 hover-lift"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </ScrollReveal>
        </div>

        {/* All testimonials preview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {testimonials.map((testimonial, index) => (
            <ScrollReveal key={testimonial.id} direction="up" delay={0.6 + index * 0.1}>
              <button
                onClick={() => goToTestimonial(index)}
                className={`p-6 rounded-2xl text-left transition-all duration-300 hover-lift ${
                  index === currentTestimonial
                    ? 'bg-white shadow-lg scale-105 border-2 border-blue-200'
                    : 'bg-white/70 hover:bg-white hover:shadow-md'
                }`}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{testimonial.name}</div>
                    <div className="text-gray-600 text-xs">{testimonial.company}</div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm line-clamp-3">
                  {testimonial.content}
                </p>
                <div className="flex items-center space-x-1 mt-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />
                  ))}
                </div>
              </button>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;